import React, { useState, useEffect } from 'react';
import { Heart, CheckCircle, AlertCircle, XCircle, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { useI18n } from '../i18n/i18n';

interface HealthStatus {
  status: 'ok' | 'degraded' | 'error';
  timestamp: string;
  services: {
    database: 'ok' | 'error';
    grafana?: 'ok' | 'error';
  };
  version: string;
  environment: string;
}

const HealthWidget: React.FC = () => {
  const { t } = useI18n();
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchHealth = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get<HealthStatus>('/health');
      setHealth(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Health check failed:', err);
      setError(t('health.error.body'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ok':
        return <CheckCircle className="h-5 w-5 text-success-500" />;
      case 'degraded':
        return <AlertCircle className="h-5 w-5 text-warning-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-error-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ok':
        return <span className="status-badge success">{t('health.status.healthy')}</span>;
      case 'degraded':
        return <span className="status-badge warning">{t('health.status.degraded')}</span>;
      case 'error':
        return <span className="status-badge error">{t('health.status.error')}</span>;
      default:
        return <span className="status-badge">{t('health.status.unknown')}</span>;
    }
  };

  return (
    <div className="card card-hover p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Heart className="h-6 w-6 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">{t('health.title')}</h3>
        </div>
        <button
          onClick={fetchHealth}
          disabled={loading}
          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error ? (
        <div className="text-center py-8">
          <XCircle className="h-12 w-12 text-error-500 mx-auto mb-3" />
          <p className="text-error-600 font-medium">{t('health.error.title')}</p>
          <p className="text-sm text-gray-500 mt-1">{error}</p>
          <button
            onClick={fetchHealth}
            className="btn-primary mt-3 text-sm"
          >
            {t('health.retry')}
          </button>
        </div>
      ) : loading && !health ? (
        <div className="text-center py-8">
          <div className="loading-spinner h-8 w-8 mx-auto mb-3"></div>
          <p className="text-gray-500">{t('health.loading')}</p>
        </div>
      ) : health ? (
        <div className="space-y-4">
          {/* Overall Status */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              {getStatusIcon(health.status)}
              <div>
                <p className="font-medium text-gray-900">{t('health.overall')}</p>
                <p className="text-sm text-gray-500">
                  {t('health.environment')} {health.environment}
                </p>
              </div>
            </div>
            {getStatusBadge(health.status)}
          </div>

          {/* Service Status */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900">{t('health.services')}</h4>
            
            {/* Database */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center space-x-2">
                {getStatusIcon(health.services.database)}
                <span className="text-gray-700">{t('health.database')}</span>
              </div>
              {getStatusBadge(health.services.database)}
            </div>

            {/* Grafana */}
            {health.services.grafana && (
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-2">
                  {getStatusIcon(health.services.grafana)}
                  <span className="text-gray-700">{t('health.grafana')}</span>
                </div>
                {getStatusBadge(health.services.grafana)}
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="pt-4 border-t border-gray-200">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">{t('health.version')}</p>
                <p className="font-medium text-gray-900">{health.version}</p>
              </div>
              <div>
                <p className="text-gray-500">{t('health.lastCheck')}</p>
                <p className="font-medium text-gray-900">
                  {lastUpdated ? 
                    lastUpdated.toLocaleTimeString() : 
                    new Date(health.timestamp).toLocaleTimeString()
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default HealthWidget;
